import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  CreateWorkloadInput,
  SyncResult,
  Workload,
  WorkloadDetail,
  WorkloadTarget,
} from '@internal/plugin-workload-common';

export interface WorkloadApi {
  list(): Promise<Workload[]>;
  get(name: string): Promise<WorkloadDetail>;
  /** The clusters a chart can be deployed to. */
  listTargets(): Promise<WorkloadTarget[]>;
  /** Deploys a Helm chart; resolves once Argo CD has the application. */
  create(input: CreateWorkloadInput): Promise<Workload>;
  /** Deletes an application the console created, and what it deployed. */
  remove(name: string): Promise<void>;
  sync(name: string, opts?: { prune?: boolean }): Promise<SyncResult>;
  refresh(name: string, hard?: boolean): Promise<SyncResult>;
}

export const workloadApiRef = createApiRef<WorkloadApi>({
  id: 'plugin.workload.service',
});

export class WorkloadClient implements WorkloadApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async base(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('workload');
  }

  /** Unwraps Backstage's error envelope so the real reason reaches the UI. */
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await this.fetchApi.fetch(`${await this.base()}${path}`, init);
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  async listTargets() {
    const r = await this.request<{ items: WorkloadTarget[] }>('/targets');
    return r.items;
  }

  async create(input: CreateWorkloadInput) {
    return this.request<Workload>('/workloads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async remove(name: string) {
    await this.request<void>(`/workloads/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }

  async list() {
    const r = await this.request<{ items: Workload[] }>('/workloads');
    return r.items;
  }

  async get(name: string) {
    return this.request<WorkloadDetail>(
      `/workloads/${encodeURIComponent(name)}`,
    );
  }

  async sync(name: string, opts: { prune?: boolean } = {}) {
    return this.request<SyncResult>(
      `/workloads/${encodeURIComponent(name)}/sync`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(opts),
      },
    );
  }

  async refresh(name: string, hard = false) {
    return this.request<SyncResult>(
      `/workloads/${encodeURIComponent(name)}/refresh`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hard }),
      },
    );
  }
}
